import classNames from "classnames";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import InternalAutopilotHydrationContext from "../../../contexts/InternalAutopilotHydrationContext";
import CiroCard from "../../shared/CiroCard";
import CuratedSearchDesignTabContainer from "./CuratedSearchDesignTabContainer";
import {
  AutopilotSearchStrategyJobEnum,
  OpensearchCuratedSearch,
} from "../../../__generated__/graphql";
import CuratedSearchDesignTab, {
  ICuratedSearchDesignTabStatus,
} from "./CuratedSearchDesignTab";

export enum CuratedSearchDesignTabStatusEnum {
  LOADING_FROM_PRIOR_SEARCH,
  LOADING_FILTERS,
  FINDING_CONTACTS,
  ANALYZING_CONTACTS,
  COMPLETED,
}

export const getStateDescription = (
  state: CuratedSearchDesignTabStatusEnum,
  remixPrompt?: string | null,
): string => {
  switch (state) {
    case CuratedSearchDesignTabStatusEnum.LOADING_FROM_PRIOR_SEARCH:
      return "Initializing";
    case CuratedSearchDesignTabStatusEnum.LOADING_FILTERS:
      return remixPrompt ? "Improving results" : "Loading filters";
    case CuratedSearchDesignTabStatusEnum.FINDING_CONTACTS:
      return "Finding contacts";
    case CuratedSearchDesignTabStatusEnum.ANALYZING_CONTACTS:
      return "Analyzing contacts";
    case CuratedSearchDesignTabStatusEnum.COMPLETED:
      return "Initial search complete";
    default:
      return "";
  }
};

const CuratedSearchDesignTabsContainer = ({
  initialPrompt,
  specificCompanies,
  initialFilters,
  savedFilters,
  savedAnalysisPreview,
  savedPreviews,
}: {
  initialPrompt: string;
  specificCompanies: string[];
  initialFilters: OpensearchCuratedSearch | null;
  savedFilters?: OpensearchCuratedSearch | null;
  savedAnalysisPreview?: any;
  savedPreviews?: any;
}) => {
  const context = useContext(InternalAutopilotHydrationContext);
  const { setSavedAnalysisPreview, setSavedPreviews, setSavedFilters } =
    context;

  // Create stable initial values that don't change after first render
  const [stableInitialFilters] = useState(initialFilters);
  const [initialSavedFilters] = useState(savedFilters);
  const [initialSavedAnalysisPreview] = useState(savedAnalysisPreview);
  const [initialSavedPreviews] = useState(savedPreviews);

  const [activeTabIdx, setActiveTabIdx] = useState<number>(0);
  const previousTabIdxRef = useRef<number>(0);

  const [tabStatus, setTabStatus] = useState<{
    [key: number]: ICuratedSearchDesignTabStatus;
  }>({
    0: {
      state: CuratedSearchDesignTabStatusEnum.LOADING_FROM_PRIOR_SEARCH,
      savedFilters: initialSavedFilters ?? {
        opensearchFiltersBlob: "",
        auxiliary_filters: {
          companyNames: specificCompanies,
          orgCompanyIds: [],
        },
      },
      savedPreviews: initialSavedPreviews,
      savedAnalysisPreview: initialSavedAnalysisPreview,
    },
    1: {
      state: CuratedSearchDesignTabStatusEnum.LOADING_FROM_PRIOR_SEARCH,
      savedFilters: initialSavedFilters ?? {
        opensearchFiltersBlob: "",
        auxiliary_filters: {
          companyNames: specificCompanies,
          orgCompanyIds: [],
        },
      },
      savedPreviews: initialSavedPreviews,
      savedAnalysisPreview: initialSavedAnalysisPreview,
    },
  });

  // Effect to handle tab changes
  useEffect(() => {
    // Restore data for new active tab from tabStatus
    const activeTabData = tabStatus[activeTabIdx];
    if (activeTabData) {
      setSavedAnalysisPreview(
        "savedAnalysisPreview" in activeTabData
          ? activeTabData.savedAnalysisPreview || null
          : null,
      );
      setSavedPreviews(
        "savedPreviews" in activeTabData
          ? activeTabData.savedPreviews || null
          : null,
      );
      setSavedFilters(
        "savedFilters" in activeTabData
          ? activeTabData.savedFilters || null
          : null,
      );
    } else {
      // Clear hydrated data if no data exists for this tab
      setSavedAnalysisPreview(null);
      setSavedPreviews(null);
      setSavedFilters(null);
    }

    // Update ref for next time
    previousTabIdxRef.current = activeTabIdx;
  }, [
    activeTabIdx,
    tabStatus,
    setSavedAnalysisPreview,
    setSavedPreviews,
    setSavedFilters,
  ]);

  const bestStrategyIdx = useMemo(() => {
    if (
      Object.values(tabStatus).length <= 1 ||
      Object.values(tabStatus).some(
        (tabStatus) =>
          tabStatus.state !== CuratedSearchDesignTabStatusEnum.COMPLETED,
      )
    ) {
      return null;
    }

    const sortedTabs = Object.entries(tabStatus).sort(
      ([, statusA], [, statusB]) => {
        const relevantContactA =
          statusA.state === CuratedSearchDesignTabStatusEnum.COMPLETED
            ? statusA.relevantContact || 0
            : 0;
        const totalContactA =
          statusA.state === CuratedSearchDesignTabStatusEnum.COMPLETED
            ? statusA.totalContact || 0
            : 0;
        const relevantContactB =
          statusB.state === CuratedSearchDesignTabStatusEnum.COMPLETED
            ? statusB.relevantContact || 0
            : 0;
        const totalContactB =
          statusB.state === CuratedSearchDesignTabStatusEnum.COMPLETED
            ? statusB.totalContact || 0
            : 0;

        if (Math.abs(relevantContactA - relevantContactB) > 2) {
          return relevantContactB - relevantContactA;
        }

        return (
          totalContactB * relevantContactB - totalContactA * relevantContactA
        );
      },
    );

    return sortedTabs.length > 0 ? parseInt(sortedTabs[0][0], 10) : null;
  }, [tabStatus]);

  const tabs = useMemo(() => {
    if (stableInitialFilters) {
      const strategy = stableInitialFilters.auxiliary_filters?.companyNames
        ?.length
        ? AutopilotSearchStrategyJobEnum.SpecificCompaniesStrategy
        : AutopilotSearchStrategyJobEnum.IndustryKeywordsStrategy;

      return [
        {
          node: (
            <CuratedSearchDesignTabContainer
              strategy={strategy}
              setTabStatus={(tabStatus) => {
                setTabStatus((prev) => ({ ...prev, 0: tabStatus }));
              }}
              tabStatus={tabStatus[0]}
              prompt={initialPrompt}
            />
          ),
          strategy,
        },
      ];
    }

    return [
      ...(!specificCompanies.length
        ? [
            {
              node: (
                <CuratedSearchDesignTabContainer
                  strategy={
                    AutopilotSearchStrategyJobEnum.IndustryKeywordsStrategy
                  }
                  setTabStatus={(tabStatus) => {
                    setTabStatus((prev) => ({ ...prev, 0: tabStatus }));
                  }}
                  tabStatus={tabStatus[0]}
                  prompt={initialPrompt}
                />
              ),
              strategy: AutopilotSearchStrategyJobEnum.IndustryKeywordsStrategy,
            },
          ]
        : []),
      {
        node: (
          <CuratedSearchDesignTabContainer
            strategy={AutopilotSearchStrategyJobEnum.SpecificCompaniesStrategy}
            setTabStatus={(tabStatus) => {
              setTabStatus((prev) => ({ ...prev, 1: tabStatus }));
            }}
            tabStatus={tabStatus[1]}
            prompt={initialPrompt}
          />
        ),
        strategy: AutopilotSearchStrategyJobEnum.SpecificCompaniesStrategy,
      },
    ];
  }, [initialPrompt, stableInitialFilters, specificCompanies, tabStatus]);

  return (
    <CiroCard size="small">
      {tabs.length > 1 && (
        <div className={classNames("mt-3", "mb-6")}>
          <p className={classNames("text-sm", "text-gray-500")}>
            Select your preferred search path below:
          </p>
        </div>
      )}
      <div
        className={classNames(
          "flex",
          "mb-4",
          "space-x-2",
          "border-b",
          "border-gray-200",
          "pb-4",
          "px-4",
          "-mx-4",
        )}
      >
        {tabs.map((tab, idx) => {
          return (
            <CuratedSearchDesignTab
              key={idx}
              activeIdx={activeTabIdx}
              tabStatus={tabStatus[idx]}
              idx={idx}
              strategy={tab.strategy}
              setActiveIdx={setActiveTabIdx}
              isBestStrategy={bestStrategyIdx === idx}
            />
          );
        })}
      </div>

      {tabs.map((tab, idx) => {
        return (
          <div
            key={idx}
            className={classNames({
              hidden: activeTabIdx !== idx,
            })}
          >
            {tab.node}
          </div>
        );
      })}
    </CiroCard>
  );
};

export default CuratedSearchDesignTabsContainer;
